import { Configuration } from "../configuration";
import { ApiError } from "../models";

const getGenericApiHeaders = (accessToken?: string): HeadersInit => {
  const apiHeaders: HeadersInit = {
    Accept: "application/json",
    "Content-Type": "application/json",
  };
  if (accessToken) {
    apiHeaders.Authorization = `Bearer ${accessToken}`;
  }
  return apiHeaders;
};

const getGenericFileApiHeaders = (accessToken?: string): HeadersInit => {
  const apiHeaders: HeadersInit = {};
  if (accessToken) {
    apiHeaders.Authorization = `Bearer ${accessToken}`;
  }
  return apiHeaders;
};

export const genericApi = {
  get: (uri: string, getApiConfiguration: () => Promise<Configuration>): Promise<any> =>
    getApiConfiguration()
      .then(config =>
        fetch(config.basePath + uri, {
          method: "GET",
          headers: getGenericApiHeaders(config.accessToken),
        }),
      )
      .then(response => {
        const contentType = response.headers.get("content-type");
        if (response.ok) {
          if (response.status === 204) {
            return undefined;
          } else if (contentType && contentType.indexOf("application/json") !== -1) {
            return response.json();
          }
        }
        // Otherwise, if a server error occurred, assume a JSON-encoded ApiError...
        else if (response.status === 500) {
          return response.json().then(error => {
            throw error;
          });
        }
        // Otherwise, some unexpected error occurred
        const unhandledError = { message: "An unhandled error occurred on GET." } as ApiError;
        throw unhandledError;
      }),

  post: (uri: string, body: any, getApiConfiguration: () => Promise<Configuration>): Promise<any> =>
    getApiConfiguration()
      .then(config =>
        fetch(config.basePath + uri, {
          method: "POST",
          headers: getGenericApiHeaders(config.accessToken),
          body: JSON.stringify(body),
        }),
      )
      .then(response => {
        const contentType = response.headers.get("content-type");
        if (response.ok) {
          if (response.status === 204) {
            return undefined;
          } else if (contentType && contentType.indexOf("application/json") !== -1) {
            return response.json();
          }
        }
        // Otherwise, if a server error occurred, assume a JSON-encoded ApiError...
        else if (response.status === 500) {
          return response.json().then(error => {
            throw error;
          });
        }
        // Otherwise, some unexpected error occurred
        const unhandledError = { message: "An unhandled error occurred on POST." } as ApiError;
        throw unhandledError;
      }),
};

export const genericFileApi = {
  get: (uri: string, getApiConfiguration: () => Promise<Configuration>): Promise<File> =>
    getApiConfiguration()
      .then(config =>
        fetch(config.basePath + uri, {
          method: "GET",
          headers: getGenericFileApiHeaders(config.accessToken),
        }),
      )
      .then(async response => {
        if (response.ok) {
          let filename = "download"; // Default filename of 'download'

          const contentTypeHeader = response.headers.get("Content-Type");
          const lastModifiedHeader = response.headers.get("Last-Modified");
          const contentDispositionHeader = response.headers.get("Content-Disposition");

          const type = contentTypeHeader || "";
          const lastModified = lastModifiedHeader ? Date.parse(lastModifiedHeader) : Date.now();
          if (contentDispositionHeader) {
            const filenameRegex = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/;
            const filenameMatches = filenameRegex.exec(contentDispositionHeader);
            if (filenameMatches && filenameMatches[1]) {
              filename = filenameMatches[1].replace(/['"]/g, "");
            }
          }

          const blob = await response.blob();
          // If the File constructor is available and this is not Edge (which has an unimplemented constructor), use it...
          // (https://developer.mozilla.org/en-US/docs/Web/API/File/File)
          if (typeof File === "function" && !/Edge/.test(navigator.userAgent)) {
            return new File([blob], filename, { type, lastModified });
          }
          // Otherwise, extend the Blob with the additional properties and return it as a File...
          else {
            const file = blob as any;
            file.name = filename;
            file.lastModified = lastModified;
            return file as File;
          }
        }
        // Otherwise, if a server error occurred, assume a JSON-encoded ApiError...
        else if (response.status === 500) {
          const error = await response.json();
          throw error;
        }
        // Otherwise, some unexpected error occurred
        const unhandledError = { message: "An unhandled error occurred on GET." } as ApiError;
        throw unhandledError;
      }),

  post: (
    uri: string,
    file: File,
    progressHandler: ((progress: number) => void) | undefined,
    getApiConfiguration: () => Promise<Configuration>,
  ): Promise<string> => {
    return getApiConfiguration().then(configuration => {
      return new Promise<string>((resolve, reject) => {
        const xhr = new XMLHttpRequest();

        xhr.onload = (event: ProgressEvent) => {
          if (xhr.status === 200) {
            const response = xhr.response as string;
            resolve(response);
          } else {
            reject(new Error("An unexpected error occurred."));
          }
        };

        xhr.onerror = (event: ProgressEvent) => {
          const error = JSON.parse(xhr.response) as ApiError;
          reject(error);
        };

        if (progressHandler) {
          xhr.upload.onprogress = (event: ProgressEvent) => {
            if (event.lengthComputable) {
              const progress = Math.min(Math.round((event.loaded / event.total) * 100), 100);

              progressHandler(progress);
            }
          };
        }

        xhr.open("POST", uri, true);

        if (configuration.accessToken) {
          xhr.setRequestHeader("Authorization", `Bearer ${configuration.accessToken}`);
        }

        const formData = new FormData();
        formData.append("file", file);

        xhr.send(formData);
      });
    });
  },
};

export const getApiError = async (error: Response | Error | unknown): Promise<ApiError> => {
  if (error instanceof Error) {
    return { message: error.message } as ApiError;
  } else if (error instanceof Response) {
    const apiError = (await error.json()) as ApiError;
    return apiError;
  }
  return { message: "An unknown error occurred." } as ApiError;
};
